import { Instance, SnapshotIn, types } from "mobx-state-tree";

export enum MapEntityStatus {
  Unchanged = 'unchanged',
  New = 'new',
  Modified = 'modified',
  Deleted = 'deleted',
}

export enum PolygonType {
  NoFly = 'no_fly',
  Limited = 'limited',
  Control = 'control',
}

export const isPolygonType = (type: PolygonType | PointType | string): boolean => {
  return Object.values(PolygonType).some(t => t === type)
}

export const isPolygon = (entity: MapEntity): entity is Polygon => {
  return isPolygonType(entity.type)
}

export enum PointType {
  Center = 'center',
  Delivery = 'delivery',
  SafeLanding = 'safe_landing',
  Pickup = 'pickup',
  Launchpad = 'launchpad',
  Dock = 'dock'
}

export const isPointType = (type: PolygonType | PointType | string): boolean => {
  return Object.values(PointType).some(t => t === type)
}

export const isPoint = (entity: MapEntity): entity is Point => {
  return isPointType(entity.type)
}

const POINT_KEYS_TO_COMPARE: (keyof SnapshotIn<Instance<typeof PointModel>>)[] = [
  'name', 'uuid', 'point', 'radius', 'letter', 'yaw', 'dock_height', 'hook_height'
];

const POLYGON_KEYS_TO_COMPARE: (keyof SnapshotIn<Instance<typeof PolygonModel>>)[] = [
  'name', 'uuid', 'polygon', 'buffer_m',
];

// private class, do not export
const Base = types.model('EntityModel').props({
  uuid: types.identifier,
  name: types.string,
  is_reviewed: types.maybeNull(types.boolean),
  is_deleted: types.boolean,
})
.volatile(() => ({
  hasErrors: false,
  /*
    `created_at` is only needed for client sorting, so that we
    put newly created entities at the top of the table
  */
  created_at: Date.now(),
}))
.volatile((self) => ({
  publishedSnapshot: null as (SnapshotIn<typeof self> | null),
}))
.views((self) => ({
  // Is entity different from published version 
  get isDirty(): boolean {
    if (self.publishedSnapshot === null) {
      return !self.is_deleted
    }

    const keysToCompare = PointModel.is(self) ? POINT_KEYS_TO_COMPARE : POLYGON_KEYS_TO_COMPARE

    // @ts-ignore
    const isEqual = keysToCompare.every((key) => { 
      return JSON.stringify(self[key]) === JSON.stringify(self.publishedSnapshot && self.publishedSnapshot[key])
    })

    return !isEqual
  },
}))
.views((self) => ({
  get status(): MapEntityStatus {
    if (self.is_deleted) {
      return MapEntityStatus.Deleted
    }

    if (self.publishedSnapshot === null) {
      return MapEntityStatus.New
    } else {
      return self.isDirty ? MapEntityStatus.Modified : MapEntityStatus.Unchanged
    }
  }
}))
.views((self) => ({
  get isDeleted(): boolean {
    return self.is_deleted
  },
  get isModified(): boolean {
    return self.status === MapEntityStatus.Modified
  },
  get isNew(): boolean {
    return self.status === MapEntityStatus.New
  },
  get isValid(): boolean {
    return !self.hasErrors
  },
  get isUnchanged(): boolean {
    return self.status === MapEntityStatus.Unchanged
  },
  get canBeDeleted(): boolean {
    return true
  },
  get requiresReview(): boolean {
    return self.is_reviewed === false
  },
  get isReviewed(): boolean {
    return self.is_reviewed === true
  },
  get doesntRequireReview(): boolean {
    return self.is_reviewed === null
  }
}))
.actions((self) => ({
  clearHasErrors(): void {
    self.hasErrors = false
  },
  clearPublishedSnapshot(): void {
    self.publishedSnapshot = null
  },
  setPublishedSnapshot(snapshot: typeof self.publishedSnapshot): void {
    self.publishedSnapshot = snapshot
  },
  markAsDeleted(): void {
    if (!self.canBeDeleted) {
      throw new Error("Can't delete this entity")
    }
    if (self.is_deleted) {
      return
    }

    self.is_deleted = true
    self.is_reviewed = false
    self.hasErrors = false // Even if entity has errors, if user deleted it it should tun grey
  },
  setHasErrors(value: boolean): void {
    self.hasErrors = value
  }
}))
.actions((self) => ({
  validate(): string | null {
    if (self.name === '') {
      return 'has empty name'
    }

    return null
  }
}))
.actions((self) => ({
  _modify<T>(key: string, value: T): void {
    self[key] = value
    
    const error = self.validate()
    self.hasErrors = Boolean(error)
    self.is_reviewed = self.isDirty ? false : null
  },
}))
.actions((self) => ({
  setName(name: string): void {
    self._modify('name', name)
  },
  setIsReviewed(isReviewed: boolean | null): void {
    self.is_reviewed = isReviewed
  },
}))

export const PolygonModel = Base.named('PolygonModel').props({
  type: types.enumeration('PolygonType', Object.values(PolygonType)),
  // Don't use `polygon` directly, use `points` getter instead
  polygon: types.array(types.array(types.float)),
  buffer_m: types.maybeNull(types.number),
})
.views((self) => ({
  get canBeDeleted(): boolean {
    return self.type !== PolygonType.Control
  },
  /*
    Return a list of unique points. This is needed because the WKT of the
    `shapely` library in Python adds the first point to the end of the list.
  */
  get points(): [number, number][] {
    const uniquePoints = new Set(self.polygon.map((point) => JSON.stringify(point)))

    return [...uniquePoints].map((point) => JSON.parse(point) as [number, number])
  }
}))
.actions((self) => ({
  setPolygon(polygon: [number, number][]): void {
    self._modify('polygon', polygon)
  },
  removePoint(index: number): void {
    if (index < 0 || index >= self.points.length) {
      throw new Error('Invalid index')
    }

    const target = self.points[index]

    self._modify('polygon', self.polygon.filter((point) => JSON.stringify(point) !== JSON.stringify(target)))
  },
}))
export type Polygon = Instance<typeof PolygonModel>

export const PointModel = Base.named('PointModel').props({
  type: types.enumeration('PointType', Object.values(PointType)),
  point: types.array(types.maybeNull(types.union(types.float, types.number))),
  letter: types.maybeNull(types.string),
  radius: types.maybeNull(types.union(types.float, types.number)),
  yaw: types.maybeNull(types.union(types.float, types.number)),
  dock_height: types.maybeNull(types.union(types.float, types.number)),
  hook_height: types.maybeNull(types.union(types.float, types.number)),
})
.views((self) => ({
  get lat(): number | null {
    return self.point[1] ?? null
  },
  get long(): number | null {
    return self.point[0] ?? null
  },
  get canBeDeleted(): boolean {
    return self.type !== PointType.Center
  }
}))
.actions((self) => ({
  validate(): string | null {
    if (self.name === '') {
      return 'has empty name'
    }

    if (self.lat === null || self.lat < -90 || self.lat > 90) {
      return 'has latitude outside of range -90 to 90'
    }

    if (self.long === null || self.long < -180 || self.long > 180) {
      return 'has longitude outside of range -180 to 180'
    }

    if (self.type == PointType.Center) 
    {
      if (self.radius === null) {
        return 'has empty radius'
      }
      if (self.radius < 0) {
        return 'has radius below 0'
      }
    }
    if (self.type == PointType.Dock) 
    {
      if (self.yaw === null) {
        return 'has empty Yaw'
      }
      if ((self.yaw < 0) || (self.yaw >= 360)){
        return 'Yaw must be >= 0 and < 360'
      }
      if (self.dock_height === null) {
        return 'has empty Dock Height'
      }
      if (self.hook_height === null) {
        return 'has empty Ground Elevation'
      }
    }

    return null
  }
}))
.actions((self) => ({
  setPoint(point: [number | null, number | null]): void {
    self._modify('point', point)
  },
  setRadius(radius: number | null): void {
    self._modify('radius', radius)
  },
  setYaw(yaw: number | null): void {
    self._modify('yaw', yaw)
  },
  setDockHeight(dock_height: number | null): void {
    self._modify('dock_height', dock_height)
  },
  setHookHeight(hook_height: number | null): void {
    self._modify('hook_height', hook_height)
  },
}))
export type Point = Instance<typeof PointModel>
export interface Launchpad extends Point { letter: string, type: PointType.Launchpad }

export const MapEntityModel = types.union(PolygonModel, PointModel)
export type MapEntity = Instance<typeof MapEntityModel>
export type MapEntityAttributes = SnapshotIn<MapEntity>

